import './rater.significant-terms-and-conditions.scss';

import {
  Box,
  FormLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableRow,
} from '@mui/material';
import {
  ConvergeSectionTitle,
  NewFromSelectField,
} from '../../../../presentation';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import {
  FusionScoreModifierDisplay,
  RaterNotesButton,
  RaterNotesRow,
} from '../common';
import {
  ModifierNoteLabel,
  NoteTargetAreaType,
  SignificantTermsConditions,
} from '@converge/graphql';
import { ReactNode, useEffect } from 'react';
import {
  toSignificantTermsConditionsValue,
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from '../data';

import { NumberField } from '../common/number.field';
import { config } from '@converge/config';
import { getParsedValue } from '../coverage-rating';
import { toTitleCase } from '@converge/data';

interface ITableCellProps extends TableCellProps {
  children?: ReactNode;
}

type Nullable<T> = T | null;

interface IRaterSignificantTermsAndConditionsProps {
  onChangeEndorsementImpact: (impact: SignificantTermsConditions) => void;
  onChangeStcSelectedModifier: (modifier: Nullable<number>) => void;
  endorsementImpact: SignificantTermsConditions;
  stcSelectedModifier?: Nullable<number>;
  stcMinModifier?: Nullable<number>;
  stcMaxModifier?: Nullable<number>;
  stcRatedModifier?: Nullable<number>;
  layout?: 'row' | 'column';
  alignStc?: 'flex-start' | 'flex-end';
  showTitle?: boolean;
  hasEndorsementImpactChanged?: boolean;
  hasStcSelectedModifierChanged?: boolean;
  isPrimary?: boolean;
}

export const EnterpriseRatingSignificantTermsAndConditions = () => {
  const {
    actions: { setEndorsementImpact, setQuoteField },
    dispatch,
  } = useRaterActionDispatchContext();

  const {
    endorsementImpact,
    stcSelectedModifier,
    stcMinModifier,
    stcMaxModifier,
    stcRatedModifier,
    hasEndorsementImpactChanged,
    hasStcSelectedModifierChanged,
  } = useRaterStateContextSelector(
    ({
      quote: {
        endorsementImpact,
        stcSelectedModifier,
        stcMinModifier,
        stcMaxModifier,
        stcRatedModifier,
        hasEndorsementImpactChanged,
        hasStcSelectedModifierChanged,
      },
    }) => {
      return {
        endorsementImpact,
        stcSelectedModifier,
        stcMinModifier,
        stcMaxModifier,
        stcRatedModifier,
        hasEndorsementImpactChanged,
        hasStcSelectedModifierChanged,
      };
    }
  );

  return (
    <RaterSignificantTermsAndConditions
      endorsementImpact={endorsementImpact}
      stcSelectedModifier={stcSelectedModifier}
      stcMinModifier={stcMinModifier}
      stcMaxModifier={stcMaxModifier}
      stcRatedModifier={stcRatedModifier}
      onChangeEndorsementImpact={(impact) => {
        dispatch(setEndorsementImpact(impact));
      }}
      onChangeStcSelectedModifier={(modifier) => {
        dispatch(setQuoteField('stcSelectedModifier', modifier ?? null));
      }}
      hasEndorsementImpactChanged={hasEndorsementImpactChanged}
      hasStcSelectedModifierChanged={hasStcSelectedModifierChanged}
      isPrimary
    />
  );
};

export const ExcessRatingSignificantTermsAndConditions = () => {
  const {
    actions: { setExcessRatingEndorsementImpact, setQuoteField },
    dispatch,
  } = useRaterActionDispatchContext();

  const {
    excessEndorsementImpact,
    stcExcessModifier,
    stcExcessMaxModifier,
    stcExcessMinModifier,
    stcExcessSelectedModifier,
    hasStcExcessSelectedModifierChanged,
    hasExcessEndorsementImpactChanged,
  } = useRaterStateContextSelector(
    ({
      quote: {
        excessEndorsementImpact,
        stcExcessModifier,
        stcExcessMaxModifier,
        stcExcessMinModifier,
        stcExcessSelectedModifier,
        hasStcExcessSelectedModifierChanged,
        hasExcessEndorsementImpactChanged,
      },
    }) => {
      return {
        excessEndorsementImpact,
        stcExcessModifier,
        stcExcessMaxModifier,
        stcExcessMinModifier,
        stcExcessSelectedModifier,
        hasStcExcessSelectedModifierChanged,
        hasExcessEndorsementImpactChanged,
      };
    }
  );

  return (
    <RaterSignificantTermsAndConditions
      endorsementImpact={
        excessEndorsementImpact ?? SignificantTermsConditions.Neutral
      }
      stcSelectedModifier={stcExcessSelectedModifier}
      stcMinModifier={stcExcessMinModifier}
      stcMaxModifier={stcExcessMaxModifier}
      stcRatedModifier={stcExcessModifier}
      onChangeEndorsementImpact={(impact) => {
        dispatch(setExcessRatingEndorsementImpact(impact));
      }}
      onChangeStcSelectedModifier={(modifier) => {
        dispatch(setQuoteField('stcExcessSelectedModifier', modifier));
      }}
      showTitle={false}
      layout="column"
      alignStc="flex-start"
      hasEndorsementImpactChanged={hasExcessEndorsementImpactChanged}
      hasStcSelectedModifierChanged={hasStcExcessSelectedModifierChanged}
    />
  );
};

export const RaterSignificantTermsAndConditions = ({
  onChangeEndorsementImpact,
  onChangeStcSelectedModifier,
  endorsementImpact,
  stcSelectedModifier,
  stcMinModifier,
  stcMaxModifier,
  stcRatedModifier,
  layout = 'column',
  alignStc = 'flex-end',
  showTitle = true,
  hasEndorsementImpactChanged,
  hasStcSelectedModifierChanged,
  isPrimary,
}: IRaterSignificantTermsAndConditionsProps) => {
  const {
    isEditable,
    isRequestingRatingUpdate,
    upsertQuoteIsLoading,
    uuid,
    inForcePolicy,
  } = useRaterStateContextSelector(
    ({
      isEditable,
      isRequestingRatingUpdate,
      upsertQuoteIsLoading,
      quote: { uuid, inForcePolicy },
    }) => {
      return {
        isEditable,
        isRequestingRatingUpdate,
        upsertQuoteIsLoading,
        uuid,
        inForcePolicy,
      };
    }
  );

  const methods = useForm();

  return (
    <>
      {showTitle && (
        <ConvergeSectionTitle mb={1}>
          Significant Terms and Conditions
        </ConvergeSectionTitle>
      )}
      <FormProvider {...methods}>
        <Stack flexDirection={layout === 'row' ? 'row' : 'column'} gap={2}>
          <Table className="enterprise-clear-score-table">
            <TableBody>
              <TableRow
                className={` ${hasEndorsementImpactChanged}
                    ? 'has-endorsement-impact-changed'
                    : 'hasNot-endorsement-impact-changed'`}
              >
                <RaterEnterpriseScoreTableBodyCell width={'25%'}>
                  Endorsement Impact
                </RaterEnterpriseScoreTableBodyCell>
                <RaterEnterpriseScoreTableBodyCell width={'25%'}>
                  <NewFromSelectField
                    disabled={
                      upsertQuoteIsLoading ||
                      !isEditable ||
                      isRequestingRatingUpdate ||
                      inForcePolicy
                    }
                    hideLabel={true}
                    name={'endorsementImpact'}
                    options={SIGNIFICANT_TERMS_CONDITIONS_OPTIONS}
                    value={
                      endorsementImpact ?? SignificantTermsConditions.Neutral
                    }
                    onChangeOption={(option) => {
                      onChangeEndorsementImpact(
                        option.value as SignificantTermsConditions
                      );
                    }}
                  />
                </RaterEnterpriseScoreTableBodyCell>
              </TableRow>
            </TableBody>
          </Table>
          <Stack sx={{ alignItems: alignStc }}>
            <FormLabel>Significant Terms and Conditions Modifier</FormLabel>
            <div className="modifierWrapper">
              <ScoreModifier
                isDisabled={
                  upsertQuoteIsLoading ||
                  !isEditable ||
                  isRequestingRatingUpdate ||
                  inForcePolicy
                }
                min={stcMinModifier}
                max={stcMaxModifier}
                rated={stcRatedModifier}
                selected={stcSelectedModifier}
                onChange={(value) => {
                  onChangeStcSelectedModifier(value);
                }}
                fallbackPlaceholder={toSignificantTermsConditionsValue(
                  endorsementImpact
                )}
                hasChanged={hasStcSelectedModifierChanged}
              />
              <RaterNotesButton
                uuid={uuid ? `${uuid}` : ''}
                openUuid={`${uuid}${
                  isPrimary
                    ? ModifierNoteLabel.StcPrimary
                    : ModifierNoteLabel.StcExcess
                }`}
                label={
                  isPrimary
                    ? [ModifierNoteLabel.StcPrimary]
                    : [ModifierNoteLabel.StcExcess]
                }
                targetArea={NoteTargetAreaType.Quote}
              />
            </div>
          </Stack>
          <RaterNotesRow
            colSpan={4}
            uuid={uuid ? `${uuid}` : ''}
            openUuid={`${uuid}${
              isPrimary
                ? ModifierNoteLabel.StcPrimary
                : ModifierNoteLabel.StcExcess
            }`}
            label={
              isPrimary
                ? [ModifierNoteLabel.StcPrimary]
                : [ModifierNoteLabel.StcExcess]
            }
            targetArea={NoteTargetAreaType.Quote}
          />
        </Stack>
      </FormProvider>
    </>
  );
};

interface IScoreModifierProps {
  isDisabled?: boolean;
  min?: Nullable<number>;
  max?: Nullable<number>;
  rated?: Nullable<number>;
  selected?: Nullable<number>;
  onChange: (value: Nullable<number>) => void;
  fallbackPlaceholder?: number;
  hasChanged?: boolean;
}

export const ScoreModifier = ({
  isDisabled,
  min,
  max,
  rated,
  selected,
  onChange,
  fallbackPlaceholder = toSignificantTermsConditionsValue(
    SignificantTermsConditions.Neutral
  ),
  hasChanged,
}: IScoreModifierProps) => {
  const { actions, dispatch } = useRaterActionDispatchContext();

  const {
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const hasErrors = (fieldName: string) => {
    return errors && errors[fieldName] ? true : false;
  };

  const hasInvalidSelectedFusionScoreModifier = hasErrors(
    'stcSelectedModifier'
  );

  useEffect(() => {
    dispatch(
      actions.setIsInvalidRaterData(hasInvalidSelectedFusionScoreModifier)
    );
  }, [actions, dispatch, hasInvalidSelectedFusionScoreModifier]);

  const handleUpdateModifier = (value: Nullable<number>) => {
    const parsedValue = getParsedValue(value);

    if (
      parsedValue !== null &&
      parsedValue !== undefined &&
      (parsedValue < (min || 0) || parsedValue > (max || 100))
    ) {
      setError(`stcSelectedModifier`, {
        type: 'stcSelectedModifier',
        message: `Fusion Score modifier must be a number between ${
          min ?? 0
        } and ${max ?? 100}`,
      });
      return;
    }
    onChange(value);
    clearErrors(`stcSelectedModifier`);
  };
  return (
    <Box
      className="fusionScore-modifier-container"
      maxWidth={150}
      minWidth={150}
    >
      <NumberField
        isDisabled={isDisabled}
        name="stcSelectedModifier"
        placeholder={(rated ?? fallbackPlaceholder).toFixed(2)}
        value={selected}
        onBlur={handleUpdateModifier}
        error={hasInvalidSelectedFusionScoreModifier}
        hasChanged={hasChanged}
      />{' '}
      <FusionScoreModifierDisplay label="Min">
        {min ?? config.defaults.significantTermsConditionsMinModifier}
      </FusionScoreModifierDisplay>
      <FusionScoreModifierDisplay label="Max">
        {max ?? config.defaults.significantTermsConditionsMaxModifier}
      </FusionScoreModifierDisplay>
    </Box>
  );
};

export const SIGNIFICANT_TERMS_CONDITIONS_OPTIONS = Object.entries(
  SignificantTermsConditions
)
  .map((significantTermsConditions) => {
    return {
      value: significantTermsConditions[1],
      label: toTitleCase(significantTermsConditions[1]),
    };
  })
  .sort((a, b) => {
    return (
      toSignificantTermsConditionsValue(a.value) -
      toSignificantTermsConditionsValue(b.value)
    );
  });

const RaterEnterpriseScoreTableBodyCell = ({
  children,
  ...otherProps
}: ITableCellProps) => {
  return (
    <TableCell className="enterprise-clear-score-cell" {...otherProps}>
      {children}
    </TableCell>
  );
};
